import React, {FC, MouseEventHandler, ReactNode, useCallback, useMemo, useRef, useState} from 'react';
import {DEBOUNCE_DELAY} from 'core/constants';
import {ShadowBlock} from 'components/ShadowBlock';
import classNames from 'classnames';
import style from './style.module.scss';

export interface IProps {
    className?: string;
    delay?: number;
    children: ReactNode;
    popup: ReactNode;
    disable?: boolean;
    willMount?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

export const Popup: FC<IProps> = ({
    willMount = false,
    disable = false,
    className,
    children,
    delay = DEBOUNCE_DELAY,
    popup,
    onOpenChange,
}) => {
    type TTimer = ReturnType<typeof setTimeout>;

    const timerRef = useRef<TTimer>();
    const [isOpen, setIsOpen] = useState(false);

    const setPopupState = useCallback(
        (nextState: boolean) => {
            setIsOpen(nextState);
            onOpenChange?.(nextState);
        },
        [onOpenChange],
    );

    const handleMouseOver = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
        clearTimeout(timerRef.current);
        setPopupState(true);
    }, [setPopupState]);

    const handleMouseLeave = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setPopupState(false);
        }, delay);
    }, [delay, setPopupState]);

    const renderPopup = useMemo<ReactNode>(() => {
        if (willMount && !isOpen) {
            return null;
        }

        return (
            <div
                className={classNames(style.container, className)}
                style={{
                    opacity: isOpen ? '1' : '0',
                    pointerEvents: isOpen ? 'auto' : 'none',
                }}
            >
                {popup}
            </div>
        );
    }, [className, isOpen, popup, willMount]);

    if (disable) {
        return <>{children}</>;
    }

    return (
        <ShadowBlock onMouseEnter={handleMouseOver} onMouseLeave={handleMouseLeave}>
            {children}
            {renderPopup}
        </ShadowBlock>
    );
};
